import { isDefined } from '@/composables/utils'
import { EndPoint } from '@/enums/EndPoint'
import { MessageType } from '@/enums/MessageType'
import { StoreId } from '@/enums/StoreId'
import { FusionStores, type NotificationMessage } from '@fusion/saas-components-v3'
import type { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import { useI18n, type Composer } from 'vue-i18n'

const DEFAULT_TIMEOUT = 5000

/**
 * Formats an error message for the snackbar.
 * @param endpoint The endpoint that caused the error.
 * @param error An Error instance.
 * @param i18n I18N instance.
 * @param timeout Numeric timeout for snackbar display.
 * @returns
 */
function formatAxiosErrorMessage(
  endpoint: EndPoint | string,
  error: any,
  i18n: Composer<{}, {}, {}, string, never, string>,
  timeout: number
): NotificationMessage {
  const name = error.response?.data?.error?.name ?? 'UnknownError'
  let text = ''

  if (name === 'UniqueConstraintError') {
    let key = `errors.axios.${endpoint}.constraintError`
    if (isDefined(error.response.data.error.fields)) {
      const fields = Object.keys(error.response.data.error.fields)
      key += `:${fields.join('_')}`
    }
    text = i18n.t(key)
  } else if (error.code === 'ERROR_NETWORK') {
    text = i18n.t(`errors.axios.${error.code.toLowerCase()}`)
  } else {
    text = error.response?.data?.error?.message ?? error.message
  }

  return { text, type: MessageType.DatabaseError, timeout }
}

export const useSnackbarStore = defineStore(StoreId.SnackBars, () => {
  // STORES, IMPORTS, & COMPOSABLES
  const i18n = useI18n({ useScope: 'global' })
  const notificationsStore = FusionStores.notifications()

  /**
   * Add an Axios message to the snackbar queue.
   * @param endpoint The endpoint that caused the error.
   * @param error An Error instance.
   * @param timeout Numeric timeout for snackbar display.
   */
  const addAxiosMessage = (
    endpoint: EndPoint | string,
    error: AxiosError,
    timeout: number = DEFAULT_TIMEOUT
  ) => {
    const errorMessage = formatAxiosErrorMessage(endpoint, error, i18n, timeout)
    const found = notificationsStore.messages.find((message: NotificationMessage) => {
      return message.text === errorMessage.text
    })
    if (!found) {
      notificationsStore.addMessage({
        ...errorMessage,
        type: MessageType.Error
      })
    }
  }

  /**
   * Add a generic message to the snackbar queue.
   * @param text Message to be displayed.
   * @param type The message type which sets an associated icon.
   * @param timeout Numeric timeout for snackbar display.
   */
  const addMessage = (
    text: string,
    type: MessageType = MessageType.Info,
    timeout: number = DEFAULT_TIMEOUT
  ) => {
    notificationsStore.addMessage({
      text,
      type: type === MessageType.DatabaseError ? MessageType.Error : type,
      timeout
    })
  }

  return { addAxiosMessage, addMessage }
})
